import { useEffect, useId, useState } from 'react';
import { AlertTriangle, Check, Loader2, Search, X } from 'lucide-react';
import { SLUG_PATTERN, toSlug } from '@pageboss/shared';
import { Button } from '@/components/ui/button';
import { InspectorField } from '@/components/inspector/inspector-field';
import { InspectorSection } from '@/components/inspector/inspector-section';
import { ImageUploadField } from '@/components/inspector/image-upload-field';
import { Toggle } from '@/components/inspector/toggle';
import { inputBase } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { PageDetail } from '@/lib/types';

// =====================================================================
// SeoDrawer — page-level SEO settings panel.
//
// Opens from the top-bar "SEO" button. Slide-in right-anchored panel
// with the same width as the block inspector (360px desktop docked,
// floating overlay on narrower viewports). Mounts at z-50 so it sits
// above the block inspector (z-30/40) when both are visible.
//
// Fields edit page.seoMeta directly via autosave.setSeoMeta. The
// renderer already merges seoMeta into the page head on publish (see
// apps/renderer/src/render.ts) and serves a sitemap.xml + robots.txt
// off the page records.
// =====================================================================

const TITLE_TARGET = { min: 30, max: 60 } as const;
const DESCRIPTION_TARGET = { min: 80, max: 160 } as const;

interface SeoDrawerProps {
  open: boolean;
  onClose: () => void;
  pageTitle: string;
  pageSlug: string;
  publicUrl: string;
  seoMeta: PageDetail['seoMeta'];
  onChange: (next: PageDetail['seoMeta']) => void;
  onCommitSlug: (next: string) => Promise<void>;
}

export function SeoDrawer({
  open,
  onClose,
  pageTitle,
  pageSlug,
  publicUrl,
  seoMeta,
  onChange,
  onCommitSlug,
}: SeoDrawerProps) {
  useEffect(() => {
    if (!open) return;
    function handle(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [open, onClose]);

  if (!open) return null;

  const meta = seoMeta ?? {};
  const patch = (next: Partial<NonNullable<PageDetail['seoMeta']>>) => {
    onChange({ ...meta, ...next });
  };

  return (
    <>
      {/* Backdrop — click-outside closes. Renders only on overlay
          breakpoints (md → xl); on xl+ the panel is docked, no
          backdrop. */}
      <div
        className="hidden md:block xl:hidden fixed inset-0 z-40 bg-text-primary/20 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />

      {/* Narrow desktop overlay (768 – 1279) */}
      <aside
        className={cn(
          'hidden md:flex md:flex-col xl:hidden',
          'fixed right-0 top-0 z-50',
          'h-dvh w-[var(--inspector-width)]',
          'border-l border-border bg-card shadow-lg',
        )}
        role="dialog"
        aria-label="Page SEO settings"
        aria-modal="true"
      >
        <SeoDrawerBody
          pageTitle={pageTitle}
          pageSlug={pageSlug}
          publicUrl={publicUrl}
          meta={meta}
          patch={patch}
          onClose={onClose}
          onClear={() => onChange(null)}
          onCommitSlug={onCommitSlug}
        />
      </aside>

      {/* Wide desktop docked panel (≥1280px). Sits inside the page-edit
          flex row as a third column when the block inspector is also
          rendered, or as a second column when not. The page-edit
          container handles layout; this aside just provides chrome. */}
      <aside
        className={cn(
          'hidden xl:flex xl:flex-col',
          'fixed right-0 top-0 z-50',
          'h-dvh w-[var(--inspector-width)]',
          'border-l border-border bg-card shadow-lg',
        )}
        role="dialog"
        aria-label="Page SEO settings"
        aria-modal="true"
      >
        <SeoDrawerBody
          pageTitle={pageTitle}
          pageSlug={pageSlug}
          publicUrl={publicUrl}
          meta={meta}
          patch={patch}
          onClose={onClose}
          onClear={() => onChange(null)}
          onCommitSlug={onCommitSlug}
        />
      </aside>
    </>
  );
}

interface SeoDrawerBodyProps {
  pageTitle: string;
  pageSlug: string;
  publicUrl: string;
  meta: NonNullable<PageDetail['seoMeta']>;
  patch: (next: Partial<NonNullable<PageDetail['seoMeta']>>) => void;
  onClose: () => void;
  onClear: () => void;
  onCommitSlug: (next: string) => Promise<void>;
}

function SeoDrawerBody({
  pageTitle,
  pageSlug,
  publicUrl,
  meta,
  patch,
  onClose,
  onClear,
  onCommitSlug,
}: SeoDrawerBodyProps) {
  // Display values fall back to the page's plain title when the SEO
  // title is empty — matches the renderer's runtime behavior (see
  // render.ts: `title: page.seoMetaJson?.title ?? page.title`).
  const displayTitle = (meta.title?.trim() || pageTitle).trim();
  const displayDescription = meta.description?.trim() ?? '';
  const titleLen = (meta.title ?? '').length;
  const descLen = (meta.description ?? '').length;

  return (
    <>
      {/* Header */}
      <header
        className={cn(
          'shrink-0',
          'flex items-center gap-2',
          'h-[var(--inspector-header-height)] px-4',
          'border-b border-border-subtle bg-card',
          'sticky top-0 z-10',
        )}
      >
        <Search className="h-5 w-5 shrink-0 text-brand-600" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="truncate text-base font-medium text-foreground leading-tight">
            SEO settings
          </div>
          <div className="truncate text-xs text-text-secondary leading-tight">
            How this page appears in search
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close SEO settings"
          className={cn(
            'inline-flex shrink-0 -mr-1.5 p-1.5 rounded',
            'text-text-secondary hover:text-foreground hover:bg-muted',
            'transition-colors duration-[var(--duration-fast)]',
          )}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto">
        {/* Page address (URL slug) */}
        <InspectorSection label="Page address">
          <SlugEditor
            pageTitle={pageTitle}
            currentSlug={pageSlug}
            publicUrl={publicUrl}
            onCommit={onCommitSlug}
          />
        </InspectorSection>

        {/* Search preview — Google-style mock card */}
        <InspectorSection label="Search preview">
          <SearchResultPreview
            url={searchPreviewUrl(publicUrl, pageSlug)}
            title={displayTitle}
            description={displayDescription}
          />
        </InspectorSection>

        {/* Fields */}
        <InspectorSection label="Search engine listing">
          <div className="flex flex-col gap-4">
            <div>
              <InspectorField
                label="Page title"
                value={meta.title ?? ''}
                placeholder={pageTitle}
                helperText={
                  titleLen === 0
                    ? `Falls back to "${pageTitle}" if blank`
                    : 'Aim for ~50–60 characters with the most important word first'
                }
                onChange={(v) => patch({ title: v })}
              />
              <CharCount value={titleLen} target={TITLE_TARGET} className="mt-1" />
            </div>
            <div>
              <InspectorField
                label="Description"
                value={meta.description ?? ''}
                multiline
                rows={3}
                placeholder="A short, plain-English sentence summarizing what this page offers."
                helperText="Shown under the title in Google search results"
                onChange={(v) => patch({ description: v })}
              />
              <CharCount value={descLen} target={DESCRIPTION_TARGET} className="mt-1" />
            </div>
          </div>
        </InspectorSection>

        {/* Social share image */}
        <InspectorSection label="Social share image">
          <ImageUploadField
            label="Open Graph image"
            url={meta.ogImageUrl ?? ''}
            onChange={(url) => patch({ ogImageUrl: url })}
          />
          <p className="mt-1 text-xs text-text-tertiary">
            Shown when the page is linked on Facebook, LinkedIn, X, iMessage, etc.
            Recommended size: 1200 × 630.
          </p>
        </InspectorSection>

        {/* Advanced */}
        <InspectorSection label="Advanced">
          <div className="flex flex-col gap-4">
            <InspectorField
              label="Canonical URL (optional)"
              type="url"
              value={meta.canonicalUrl ?? ''}
              placeholder="https://www.example.com/your-page"
              helperText="Set if this page exists at another URL too (rare)"
              onChange={(v) => patch({ canonicalUrl: v })}
            />
            <Toggle
              label="Hide from search engines"
              description="Adds a noindex meta tag. Pages stay reachable by direct link."
              checked={Boolean(meta.noIndex)}
              onChange={(checked) => patch({ noIndex: checked })}
            />
          </div>
        </InspectorSection>

        <InspectorSection>
          <Button
            type="button"
            variant="ghost"
            onClick={onClear}
            className="h-auto px-2 py-1 text-xs text-text-tertiary hover:bg-danger-bg hover:text-danger-fg"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Reset SEO settings to defaults
          </Button>
        </InspectorSection>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------
// Search preview card — Google-style mock.
// ---------------------------------------------------------------------

interface SearchResultPreviewProps {
  url: string;
  title: string;
  description: string;
}

function SearchResultPreview({ url, title, description }: SearchResultPreviewProps) {
  const titleTrimmed = truncate(title, 60);
  const descTrimmed = description
    ? truncate(description, 160)
    : 'Add a description to control how this page appears in search results.';
  return (
    <div className="rounded-[var(--radius)] border border-border-subtle bg-card p-3">
      <div className="text-xs text-text-tertiary truncate">{url}</div>
      <div className="mt-1 text-[15px] leading-snug text-[oklch(0.40_0.13_265)] line-clamp-2">
        {titleTrimmed}
      </div>
      <div className="mt-1 text-[13px] leading-relaxed text-text-secondary line-clamp-3">
        {descTrimmed}
      </div>
    </div>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  // Cut at the nearest word boundary before max so the preview reads
  // like Google's actual truncation.
  const cut = s.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…';
}

function searchPreviewUrl(publicUrl: string, pageSlug: string): string {
  // publicUrl already encodes the full page URL. Strip protocol for
  // the SERP-style display (`example.com › path › page`).
  try {
    const u = new URL(publicUrl);
    const path = u.pathname.replace(/\/$/, '');
    if (!path) return u.hostname;
    return `${u.hostname}${path.replaceAll('/', ' › ')}`;
  } catch {
    return `${publicUrl} / ${pageSlug}`;
  }
}

// ---------------------------------------------------------------------
// CharCount — inline `X / Y` counter that turns clay-red beyond max
// and warns when below min target.
// ---------------------------------------------------------------------

interface CharCountProps {
  value: number;
  target: { min: number; max: number };
  className?: string;
}

function CharCount({ value, target, className }: CharCountProps) {
  let tone: 'tertiary' | 'good' | 'short' | 'over' = 'tertiary';
  if (value === 0) tone = 'tertiary';
  else if (value > target.max) tone = 'over';
  else if (value < target.min) tone = 'short';
  else tone = 'good';

  const label =
    tone === 'over'
      ? `${value} / ${target.max} — too long`
      : tone === 'short'
        ? `${value} / ${target.max} — aim for at least ${target.min}`
        : tone === 'good'
          ? `${value} / ${target.max} — good length`
          : `0 / ${target.max}`;

  const id = useId();
  return (
    <p
      id={id}
      className={cn(
        'text-xs leading-tight',
        tone === 'over' && 'text-danger-fg font-medium',
        tone === 'good' && 'text-success-fg',
        tone === 'short' && 'text-warning-fg',
        tone === 'tertiary' && 'text-text-tertiary',
        className,
      )}
    >
      {label}
    </p>
  );
}

// ---------------------------------------------------------------------
// SlugEditor — page URL slug rename, explicit submit (not autosaved).
// ---------------------------------------------------------------------

interface SlugEditorProps {
  pageTitle: string;
  currentSlug: string;
  publicUrl: string;
  onCommit: (next: string) => Promise<void>;
}

function SlugEditor({ pageTitle, currentSlug, publicUrl, onCommit }: SlugEditorProps) {
  const id = useId();
  const [draft, setDraft] = useState(currentSlug);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const isHome = currentSlug === 'home';

  // Re-sync the input when the server's current slug changes (e.g.
  // after an undo of a rename or a different tab editing the page).
  useEffect(() => {
    setDraft(currentSlug);
    setServerError(null);
    setJustSaved(false);
  }, [currentSlug]);

  const trimmed = draft.trim();
  const formatIssue = validatePageSlug(trimmed);
  const unchanged = trimmed === currentSlug;
  const canCommit = !formatIssue && !unchanged && !submitting;
  // T12 fix round 1 (F4): the commit button is only the filled primary once
  // the slug actually differs from the saved one and can be committed — the
  // same outline-until-dirty rule T8 applied to the Website screen's Save.
  // A disabled primary is never the only filled button on a screen, and an
  // unchanged slug offers nothing to commit.
  const commitIsPrimary = canCommit || submitting;
  const suggested = toSlug(pageTitle);
  const showSuggestion =
    !!pageTitle && suggested.length > 0 && suggested !== trimmed && suggested !== currentSlug;

  const handleCommit = async () => {
    if (!canCommit) return;
    setSubmitting(true);
    setServerError(null);
    setJustSaved(false);
    try {
      await onCommit(trimmed);
      setJustSaved(true);
      // Briefly show success then clear so the operator can rename
      // again without manually dismissing.
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      setServerError(messageFromError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' && canCommit) {
      e.preventDefault();
      void handleCommit();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        URL slug
      </label>
      <div className="flex items-stretch gap-2">
        <input
          id={id}
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setServerError(null);
            setJustSaved(false);
          }}
          onKeyDown={onKeyDown}
          spellCheck={false}
          autoCapitalize="none"
          autoComplete="off"
          // ONE input recipe (design audit T12/B2 → T14). The monospace face
          // and the invalid-slug border are this field's own; everything
          // else comes from inputBase.
          className={cn(
            inputBase,
            'flex-1 min-w-0 h-9 py-1.5 font-mono',
            (formatIssue || serverError) && draft !== currentSlug && 'border-danger',
          )}
        />
        <Button
          type="button"
          size="sm"
          variant={commitIsPrimary ? 'default' : 'outline'}
          onClick={handleCommit}
          disabled={!canCommit}
          className="shrink-0"
        >
          {submitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : justSaved ? (
            <Check className="h-3.5 w-3.5" aria-hidden />
          ) : null}
          {submitting ? 'Updating…' : justSaved ? 'Updated' : 'Update URL'}
        </Button>
      </div>

      <p className="text-xs text-text-tertiary truncate" aria-live="polite">
        Live URL: {previewUrl(publicUrl, currentSlug, trimmed)}
      </p>

      {isHome ? (
        <p className="inline-flex items-start gap-1.5 text-xs text-warning-fg">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
          Renaming the home page breaks your site's root URL — old links to it stop working.
        </p>
      ) : null}

      {formatIssue && !unchanged ? (
        <p className="text-xs text-danger-fg">{formatIssue}</p>
      ) : showSuggestion ? (
        <p className="text-xs text-text-tertiary">
          Based on the page title, try{' '}
          <button
            type="button"
            onClick={() => {
              setDraft(suggested);
              setServerError(null);
            }}
            className="font-mono text-brand-700 hover:underline"
          >
            {suggested}
          </button>
        </p>
      ) : !formatIssue && !unchanged && !serverError ? (
        <p className="text-xs text-text-tertiary">
          Press Enter or click Update to rename. The old URL will return 404 after rename.
        </p>
      ) : null}

      {serverError ? <p className="text-xs text-danger-fg">{serverError}</p> : null}
    </div>
  );
}

// Validate a slug for the page-rename UI. Mirrors the server's
// slugSchema constraints so the operator gets immediate feedback
// before the PATCH fires.
function validatePageSlug(slug: string): string | null {
  if (slug.length === 0) return 'URL slug can\'t be empty.';
  if (slug.length < 2) return 'Use at least 2 characters.';
  if (slug.length > 64) return 'Keep it under 64 characters.';
  if (!SLUG_PATTERN.test(slug)) {
    return 'Lowercase letters, numbers, and single hyphens only — no spaces or punctuation.';
  }
  return null;
}

function previewUrl(publicUrl: string, currentSlug: string, draftSlug: string): string {
  // Substitute the slug fragment of the existing publicUrl with the
  // draft so the operator sees the actual rename target. Home-page
  // case: publicUrl has no trailing slug; show /(home) so the
  // operator understands the URL doesn't change for the home slug
  // until they switch it.
  try {
    const u = new URL(publicUrl);
    const path = u.pathname.replace(/\/$/, '');
    const segments = path.split('/').filter(Boolean);
    if (currentSlug === 'home') {
      // publicUrl: https://web.pageboss.com/{site}
      return draftSlug === 'home'
        ? `${u.hostname}${path}`
        : `${u.hostname}${path}/${draftSlug}`;
    }
    // publicUrl: https://web.pageboss.com/{site}/{page}
    if (segments.length >= 2) {
      segments[segments.length - 1] = draftSlug === 'home' ? '' : draftSlug;
      const next = '/' + segments.filter(Boolean).join('/');
      return `${u.hostname}${next}`;
    }
    return `${u.hostname}${path}/${draftSlug}`;
  } catch {
    return `${publicUrl}/${draftSlug}`;
  }
}

function messageFromError(err: unknown): string {
  if (err instanceof Error) {
    // api wrapper attaches a friendly `message` on known errors. The
    // server's 409 includes `{ error: 'slug_taken' }` which our api
    // helper turns into Error("slug_taken"); translate that to a UX
    // message here.
    if (err.message === 'slug_taken') {
      return 'Another page on this site already uses that URL slug.';
    }
    if (err.message === 'invalid_input') {
      return 'That URL slug isn\'t valid — use lowercase letters, numbers, and single hyphens.';
    }
    return err.message;
  }
  return 'Could not update the URL.';
}
